import { apiRequest } from './apiUtils';
import type {
  WalletAccessTokenResponse,
  WalletBootstrapResponse,
  WalletPayoutDetailResponse,
  WalletPendingBreakdown,
  WalletPendingBreakdownResponse,
  WalletPendingPayoutItem,
  WalletSetupAccountResponse,
  WalletWithdrawalItem,
} from '@foodfluence/shared';

export type {
  WalletAccessTokenResponse,
  WalletBootstrapResponse,
  WalletPayoutDetailResponse,
  WalletPendingBreakdown,
  WalletPendingBreakdownResponse,
  WalletPendingPayoutItem,
  WalletSetupAccountResponse,
  WalletWithdrawalItem,
};

export interface LcOrderItem {
  id: string;
  brandName: string;
  brandLogoUrl?: string;
  cash: number;
  completedAt: string;
  payoutClaimed: boolean;
}

export interface WalletAvailableBalanceResponse {
  availableBalance: number;
}

/** Session fields used by wallet UI (subset of bootstrap). */
export type WalletSessionResponse = Pick<
  WalletBootstrapResponse,
  'token' | 'companyId' | 'availableBalance' | 'kycApproved' | 'tokenExpiresAt' | 'requiresHttps'
>;

export interface WalletRequestOptions {
  /** Viewed influencer — required for superuser switched-account sessions. */
  influencerId?: string;
}

export const walletApi = {
  getAccessToken: async (options?: WalletRequestOptions): Promise<WalletAccessTokenResponse> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/wallet/token',
      influencerId: options?.influencerId,
    });
  },

  getAvailableBalance: async (options?: WalletRequestOptions): Promise<WalletAvailableBalanceResponse> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/wallet/balance',
      influencerId: options?.influencerId,
    });
  },

  bootstrap: async (options?: WalletRequestOptions): Promise<WalletBootstrapResponse> => {
    const baseUrl = window.location.origin;
    return apiRequest({
      method: 'POST',
      endpoint: `/wallet/bootstrap?baseUrl=${encodeURIComponent(baseUrl)}`,
      influencerId: options?.influencerId,
    });
  },

  setupAccount: async (options?: WalletRequestOptions): Promise<WalletSetupAccountResponse> => {
    return apiRequest({
      method: 'POST',
      endpoint: '/wallet/setup-account',
      influencerId: options?.influencerId,
    });
  },

  getLcOrders: async (options?: WalletRequestOptions): Promise<{ orders: LcOrderItem[] }> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/wallet/lc-orders',
      influencerId: options?.influencerId,
    });
  },

  getPendingBreakdown: async (options?: WalletRequestOptions): Promise<WalletPendingBreakdownResponse> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/wallet/pending-breakdown',
      influencerId: options?.influencerId,
    });
  },

  getWithdrawals: async (options?: WalletRequestOptions): Promise<{ withdrawals: WalletWithdrawalItem[] }> => {
    return apiRequest({
      method: 'GET',
      endpoint: '/wallet/withdrawals',
      influencerId: options?.influencerId,
    });
  },

  getPayoutDetail: async (
    payoutId: string,
    options?: WalletRequestOptions,
  ): Promise<WalletPayoutDetailResponse> => {
    return apiRequest({
      method: 'GET',
      endpoint: `/wallet/payouts/${encodeURIComponent(payoutId)}`,
      influencerId: options?.influencerId,
    });
  },
};
